import type { ApiClient } from '@classgo/app-kit';
import type {
  CreateImportInput,
  ImportDryRunInput,
  ImportDryRunResult,
  ImportEntity,
  ImportJobView,
  ImportTemplate,
  ImportTemplateSummary,
} from '@classgo/shared';
import { queryOptions } from '@tanstack/react-query';

/**
 * The import endpoints (tickets #60 and #61): templates, dry run, upload, runs.
 *
 * @param {ApiClient} api -- The client.
 * @returns The calls.
 */
export const importsApi = (api: ApiClient) => ({
  template: (entity: ImportEntity, fill: 'examples' | 'tenant' = 'examples') =>
    api.request<ImportTemplate>(`/imports/templates/${entity}?fill=${fill}`),
  dryRun: (body: ImportDryRunInput) =>
    api.request<ImportDryRunResult>('/imports/dry-run', { method: 'POST', body }),
  presign: (filename: string, sizeBytes: number) =>
    api.request<{ key: string; url: string; expiresAt: string }>('/uploads/presign', {
      method: 'POST',
      body: { purpose: 'import', filename, contentType: 'text/csv', sizeBytes },
    }),
  create: (body: CreateImportInput) =>
    api.request<ImportJobView>('/imports', { method: 'POST', body }),
});

/**
 * Query options of the import screens.
 */
export const importQueries = {
  templates: (api: ApiClient) =>
    queryOptions({
      queryKey: ['imports', 'templates'],
      queryFn: () => api.request<{ items: ImportTemplateSummary[] }>('/imports/templates'),
    }),
  job: (api: ApiClient, id: string) =>
    queryOptions({
      queryKey: ['imports', 'jobs', id],
      queryFn: () => api.request<ImportJobView>(`/imports/${id}`),
      enabled: id !== '',
      refetchInterval: (query) => {
        const status = query.state.data?.status;
        return status === 'queued' || status === 'running' ? 2500 : false;
      },
    }),
  jobs: (api: ApiClient) =>
    queryOptions({
      queryKey: ['imports', 'jobs'],
      queryFn: () => api.request<{ items: ImportJobView[] }>('/imports'),
    }),
};

/**
 * A file's bytes as base64, the way `POST /imports/dry-run` expects them (Latin-1 and UTF-16
 * exports survive the trip).
 *
 * @param {Blob} file -- The chosen file.
 * @returns The base64 text.
 */
export const fileToBase64 = async (file: Blob): Promise<string> => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  }
  return btoa(binary);
};

/**
 * Offers a CSV text as a download in the browser.
 *
 * @param {string} filename -- The download name.
 * @param {string} csv -- The CSV text (with its BOM).
 */
export const downloadCsv = (filename: string, csv: string): void => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
